<template>
  <DetailPage>
    <template #left-nav>
      <button class="link link-main icon-link" @click="cancelDialog">
        <Icon icon="left" /> {{ $t('GENERAL.BACK') }}
      </button>
    </template>
    <template #central-nav>
      <h2>{{ $t('PUBLISHING.DETAIL.PRODUCTS.SETTINGS_DIALOG.HEADING') }}</h2>
      <CompositionModeBadge :composition-mode="compositionMode" />
    </template>
    <template #right-nav>
      <ProgressButton
        class="btn btn-primary save-btn"
        :on-click="save"
        label="GENERAL.SAVE"
        :disabled="invalidSettings"
      />
    </template>
    <template #content>
      <div v-if="initialized" class="product-settings">
        <ConfigSection
          heading="PUBLISHING.DETAIL.PRODUCTS.CATEGORIES.HEADING"
          subheading="PUBLISHING.DETAIL.PRODUCTS.CATEGORIES.SELECTION_TEXT"
          :error-text="
            invalidCategoriesSetting
              ? 'PUBLISHING.DETAIL.PRODUCTS.VALIDATION.AT_LEAST_ONE'
              : null
          "
        >
          <ProductSettingsCategories
            :composition="composition"
            :selected-categories="settings.categories"
            @on-select="onCategorySelection"
          />
        </ConfigSection>

        <ConfigSection
          v-if="showApparelSettings"
          heading="PUBLISHING.DETAIL.PRODUCTS.APPAREL.HEADING"
        >
          <ConfigSection
            heading="PUBLISHING.DETAIL.PRODUCTS.TARGET_GROUP.HEADING"
            subheading="PUBLISHING.DETAIL.PRODUCTS.TARGET_GROUP.SELECTION_TEXT"
            display-inline
            secondary
            :error-text="
              invalidLabelsSetting
                ? 'PUBLISHING.DETAIL.PRODUCTS.VALIDATION.AT_LEAST_ONE'
                : null
            "
          >
            <ProductSettingsTargetGroups
              :composition="composition"
              :selected-labels="settings.labels"
              @on-select="onLabelSelection"
            />
          </ConfigSection>
          <div class="spacer"></div>
          <ConfigSection
            heading="PUBLISHING.DETAIL.PRODUCTS.DEFAULT_POSITION.HEADING"
            subheading="PUBLISHING.DETAIL.PRODUCTS.DEFAULT_POSITION.SELECTION_TEXT"
            display-inline
            secondary
          >
            <ProductSettingsMPAHotspots
              v-if="hasMultipleDesigns"
              :main-design-id="composition.mainDesign.id"
              :hotspot-configs="settings.hotspotConfigs"
              display-hotspot-images
              @on-default-select="onDefaultHotspotSelection"
              @on-hotspot-configs-selection="onHotspotConfigsSelection"
            />
            <ProductSettingsHotspots
              v-else
              :composition="composition"
              :selected-hotspot="settings.defaultHotspot"
              display-hotspot-images
              @on-default-select="onDefaultHotspotSelection"
            />
          </ConfigSection>
        </ConfigSection>

        <ConfigSection
          heading="PUBLISHING.DETAIL.PRODUCTS.DEFAULT_COLOR.HEADING"
          subheading="PUBLISHING.DETAIL.PRODUCTS.DEFAULT_COLOR.SELECTION_TEXT"
        >
          <ProductSettingsAppearanceGroups
            :composition="composition"
            :selected-appearance-group="settings.appearanceGroup"
            @on-select="onAppearanceGroupSelection"
          />
        </ConfigSection>
        <ProductSettingsFeedback />
        <div class="spacer"></div>
      </div>
    </template>
  </DetailPage>
</template>

<script>
import { mapState } from 'vuex';
import { mapState as mapPiniaState } from 'pinia';
import ProgressButton from 'commons/btnProgress/ProgressButton.vue';
import DetailPage from 'src/app/components/detailPage/DetailPage.vue';
import ConfigSection from 'src/app/components/publishing/ConfigSection.vue';
import ProductSettingsHotspots from '../ProductSettingsHotspots.vue';
import ProductSettingsAppearanceGroups from '../ProductSettingsAppearanceGroups.vue';
import ProductSettingsCategories from '../ProductSettingsCategories.vue';
import ProductSettingsTargetGroups from '../ProductSettingsTargetGroups.vue';
import compositionHelper from 'commons/composition/compositionHelper';
import unsavedChangesService from 'commons/unsavedChangesService/unsavedChangesService';
import { deepCopy, isEqual } from 'commons/utils';
import {
  getApparelTargetGroupLabels,
  hasApparelCategory,
} from '@spreadgroup/common-partner-lib/composition';
import { usePublishingStore } from 'commons/stores/publishing';
import ProductSettingsMPAHotspots from 'src/app/partnerarea/publishing/detail/productSettings/ProductSettingsMPAHotspots.vue';
import { useCompositionStore } from 'commons/stores/composition';
import CompositionModeBadge from 'src/app/components/publishing/statusBadges/CompositionModeBadge.vue';
import ProductSettingsFeedback from '../ProductSettingsFeedback.vue';

export default {
  name: 'ProductSettingsSmart',
  components: {
    ProductSettingsMPAHotspots,
    ProgressButton,
    DetailPage,
    ConfigSection,
    ProductSettingsHotspots,
    ProductSettingsAppearanceGroups,
    ProductSettingsCategories,
    ProductSettingsTargetGroups,
    CompositionModeBadge,
    ProductSettingsFeedback,
  },
  emits: ['onClose', 'onDismiss'],
  data() {
    return {
      settings: {
        hotspotConfigs: null,
        defaultHotspot: null,
        appearanceGroup: null,
        categories: null,
        labels: null,
      },
      originalSettings: null,
      initialized: false,
    };
  },
  computed: {
    ...mapPiniaState(usePublishingStore, ['currentScope']),
    ...mapPiniaState(useCompositionStore, {
      composition: (store) => store.current,
      compositionMode: (store) => store.mode,
    }),
    ...mapState({
      apparelTargetGroupLabels: (s) =>
        s.publishingCore.apparelTargetGroupLabels,
    }),

    showApparelSettings() {
      return hasApparelCategory(this.settings.categories);
    },

    invalidCategoriesSetting() {
      return !this.settings.categories.length;
    },
    invalidLabelsSetting() {
      return !this.settings.labels.length;
    },
    invalidSettings() {
      return this.invalidCategoriesSetting || this.invalidLabelsSetting;
    },
    hasMultipleDesigns() {
      return compositionHelper.hasMultipleDesigns(this.composition);
    },
  },
  async created() {
    this.settings.defaultHotspot = compositionHelper.getDefaultHotspot(
      this.composition,
    );
    this.settings.hotspotConfigs = compositionHelper.getHotspotConfigs(
      this.composition,
    );
    this.settings.appearanceGroup = compositionHelper.getDefaultAppearanceGroup(
      this.composition,
    )?.id;
    this.settings.categories = compositionHelper.getCategories(
      this.composition,
    );
    this.settings.labels = getApparelTargetGroupLabels(
      this.apparelTargetGroupLabels,
      this.composition,
    );
    this.originalSettings = deepCopy(this.settings);
    this.initialized = true;
  },
  methods: {
    back() {},
    async save() {
      const compositionStore = useCompositionStore();
      await compositionStore.updateCompositionProductSettings({
        hotspotConfigs: this.hasMultipleDesigns
          ? this.settings.hotspotConfigs
          : null,
        defaultHotspot: this.settings.defaultHotspot,
        defaultAppearanceGroup: this.settings.appearanceGroup,
        categories: this.settings.categories,
        labels: this.settings.labels,
      });

      this.$emit('onClose');
    },
    async onHotspotConfigsSelection(hotspotConfigs) {
      this.settings.hotspotConfigs = hotspotConfigs;
    },
    async onDefaultHotspotSelection(hotspot) {
      this.settings.defaultHotspot = hotspot;
    },
    async onAppearanceGroupSelection(appearanceGroup) {
      this.settings.appearanceGroup = appearanceGroup;
    },
    async onCategorySelection(category) {
      const included = this.settings.categories.some(
        (cat) => cat.id === category.id,
      );
      if (included) {
        this.settings.categories = this.settings.categories.filter(
          (cat) => cat.id !== category.id,
        );
      } else {
        this.settings.categories.push(category);
      }
    },
    async onLabelSelection(label) {
      const included = this.settings.labels.some((lab) => lab.id === label.id);
      if (included) {
        this.settings.labels = this.settings.labels.filter(
          (lab) => lab.id !== label.id,
        );
      } else {
        this.settings.labels.push(label);
      }
    },
    async cancelDialog() {
      unsavedChangesService.triggerCheck({
        check: () => !isEqual(this.settings, this.originalSettings),
        save: () => {
          this.save();
        },
        cancel: () => {
          this.$emit('onDismiss');
        },
        unchanged: () => {
          this.$emit('onDismiss');
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/links';

.product-settings {
  margin: 24px 0 0 0;
  padding: 0 24px;

  strong {
    display: inline-block;
    margin: 0 0 16px 0;
  }
}

.spacer {
  height: 24px;
}
</style>
